// BossBar — top-center boss set-piece HUD (HUD-restyle Lane D.3).
//
// The missing UI half of the hollow-coil boss fight. A wide ink&brass bar
// that appears when a boss-phased mob starts transitioning phases, shows the
// boss name in Cinzel + phase pips (P1/P2/P3…) + an HP track, advances a pip
// with a brief shake on each phase change, warms its brass accent with the
// BOSS_MOOD swell at LOW intensity, and dismisses cleanly on defeat.
//
// Data seams (all live, verified 2026-07-02):
//   EVENTS.BOSS_PHASE_CHANGED  → appear + pip advance + shake + name/color,
//                                and the phase's rock armour window (armorMs /
//                                armorDamageMultiplier) as the sub-line
//                                "Rock Armour −50%" while it lasts (plan 15 §2.4:
//                                it used to float over the golem; the in-world
//                                ring already marks the armour)
//   EVENTS.BOSS_MOOD           → subtle brass-accent swell (BossMoodDriver)
//   EVENTS.NPC_DEFEATED        → clean dismiss (matched by npcId)
//   EVENTS.BOSS_PHASE_RESET    → dismiss: the boss leashed home (plan 07)
//   window.__COLYSEUS_ROOM__   → tracked npc's room-state HP (health/maxHealth)
//
// It is a THEMED React HUD component — no new styling runtime (style guide §7).

import React, { useEffect, useReducer, useRef, useState } from 'react';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import type {
  BossPhaseChangeEvent,
  BossPhaseResetEvent,
  NpcDefeatedEvent,
} from '../../../shared/types/NetworkMessages';
import {
  ACCENT,
  CHROME,
  COLORS,
  FONTS,
  FONT_SIZE,
  FONT_WEIGHT,
  RADII,
  SHADOW,
  SPACE,
} from '../theme';

// ── phase state (pure reducer — testable independent of the DOM) ────────────

interface BossBarState {
  /** Tracked boss npc id — null = hidden. */
  npcId: string | null;
  npcName: string;
  /** Current phase's display color (from the phase event). */
  color: string;
  /** Current phase's name (subtitle). */
  phaseName: string;
  /** # of BOSS_PHASE_CHANGED received for this boss (≥1 once visible). The
   *  boss starts in its base rotation ("P1"); the first threshold crossing is
   *  the move into "P2", so the lit pip is `phasesReached + 1`. */
  phasesReached: number;
  /** Bumped every phase change to retrigger the shake animation. */
  shakeSeq: number;
  /** Epoch ms the current phase's rock armour ends; 0 = no armour. */
  armourUntil: number;
  /** Armour damage reduction in whole percent (50 = "−50%"); 0 = unknown. */
  armourPct: number;
}

/** The hidden/initial reducer state (exported for tests). */
export const BOSS_BAR_HIDDEN: BossBarState = {
  npcId: null,
  npcName: '',
  color: ACCENT.brassBright,
  phaseName: '',
  phasesReached: 0,
  shakeSeq: 0,
  armourUntil: 0,
  armourPct: 0,
};

type BossBarAction =
  /** `nowMs` (epoch) anchors the armour window; defaults to Date.now(). */
  | { kind: 'phase'; msg: BossPhaseChangeEvent; nowMs?: number }
  | { kind: 'defeat'; npcId: string }
  /** The boss leashed home and healed (plan 07): the fight is over. */
  | { kind: 'reset'; npcId: string };

export function bossBarReducer(state: BossBarState, action: BossBarAction): BossBarState {
  switch (action.kind) {
  case 'phase': {
    const { msg } = action;
    if (!msg?.npcId) return state;
    const sameBoss = state.npcId === msg.npcId;
    const armorMs = typeof msg.armorMs === 'number' && msg.armorMs > 0 ? msg.armorMs : 0;
    const mult = msg.armorDamageMultiplier;
    return {
      npcId: msg.npcId,
      npcName: msg.npcName || state.npcName || 'Boss',
      color: msg.color || ACCENT.brassBright,
      phaseName: msg.phaseName || '',
      phasesReached: sameBoss ? state.phasesReached + 1 : 1,
      shakeSeq: state.shakeSeq + 1,
      armourUntil: armorMs > 0 ? (action.nowMs ?? Date.now()) + armorMs : 0,
      armourPct: armorMs > 0 && typeof mult === 'number' && mult < 1 ? Math.round((1 - mult) * 100) : 0,
    };
  }
  case 'defeat':
  case 'reset':
    // Only the tracked boss's defeat (or leash reset) dismisses the bar.
    return action.npcId === state.npcId ? { ...BOSS_BAR_HIDDEN, shakeSeq: state.shakeSeq } : state;
  default:
    return state;
  }
}

/** The armour sub-line ("Rock Armour −50%") while the window is open, else null. */
export function armourSubline(state: Pick<BossBarState, 'armourUntil' | 'armourPct'>, nowMs: number): string | null {
  if (state.armourUntil <= nowMs) return null;
  return state.armourPct > 0 ? `Rock Armour −${state.armourPct}%` : 'Rock Armour';
}

/** Total pips to draw + which one is lit (1-based). Base phase = P1; each
 *  threshold event lights the next. Grows past 3 for many-phase bosses, but
 *  defaults to the 3-pip P1/P2/P3 shape the mock/Keeper use. */
export function pipLayout(phasesReached: number): { total: number; active: number } {
  const active = phasesReached + 1; // base(1) + thresholds crossed
  return { total: Math.max(3, active), active };
}

// ── HP readout from live room state (degrades to null off-room) ─────────────

function readBossHpFraction(npcId: string | null): number | null {
  if (!npcId || typeof window === 'undefined') return null;
  try {
    const room = (window as unknown as { __COLYSEUS_ROOM__?: any }).__COLYSEUS_ROOM__;
    const npc = room?.state?.npcs?.get?.(npcId);
    if (!npc || !npc.maxHealth) return null;
    return Math.max(0, Math.min(1, npc.health / npc.maxHealth));
  } catch {
    return null;
  }
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

// ── keyframes (injected once with the component) ────────────────────────────

const BOSSBAR_KEYFRAMES = `
@keyframes bossbar-shake {
  0%,100% { transform: translateX(0); }
  20% { transform: translateX(-3px); }
  40% { transform: translateX(3px); }
  60% { transform: translateX(-2px); }
  80% { transform: translateX(2px); }
}`;

// ── component ───────────────────────────────────────────────────────────────

export const BossBar: React.FC = () => {
  const [state, dispatch] = useReducer(bossBarReducer, BOSS_BAR_HIDDEN);
  const [hpFrac, setHpFrac] = useState<number | null>(null);
  const [moodIntensity, setMoodIntensity] = useState(1);
  const reduceMotion = useRef(prefersReducedMotion());
  // Re-render once when the armour window closes, to drop its sub-line.
  const [, armourExpired] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    const left = state.armourUntil - Date.now();
    if (left <= 0) return;
    const id = setTimeout(armourExpired, left + 16);
    return () => clearTimeout(id);
  }, [state.armourUntil]);

  // Subscribe to the boss seams once.
  useEffect(() => {
    let bus: EventBus;
    try {
      bus = EventBus.getInstance();
    } catch {
      return; // no bus (some harnesses) — stay hidden
    }
    const offPhase = bus.on(EVENTS.BOSS_PHASE_CHANGED, (msg: BossPhaseChangeEvent) =>
      dispatch({ kind: 'phase', msg }),
    );
    const offDefeat = bus.on(EVENTS.NPC_DEFEATED, (msg: NpcDefeatedEvent) => {
      if (msg?.npcId) dispatch({ kind: 'defeat', npcId: msg.npcId });
    });
    const offReset = bus.on(EVENTS.BOSS_PHASE_RESET, (msg: BossPhaseResetEvent) => {
      if (msg?.npcId) dispatch({ kind: 'reset', npcId: msg.npcId });
    });
    const offMood = bus.on(EVENTS.BOSS_MOOD, (d: { intensity?: number }) => {
      if (typeof d?.intensity === 'number' && Number.isFinite(d.intensity)) {
        setMoodIntensity(d.intensity);
      }
    });
    return () => {
      offPhase();
      offDefeat();
      offReset();
      offMood();
    };
  }, []);

  // Poll the tracked boss's live HP while visible.
  const npcId = state.npcId;
  useEffect(() => {
    if (!npcId) {
      setHpFrac(null);
      return;
    }
    setHpFrac(readBossHpFraction(npcId));
    const id = setInterval(() => setHpFrac(readBossHpFraction(npcId)), 150);
    return () => clearInterval(id);
  }, [npcId]);

  if (!state.npcId) return null;

  const { total, active } = pipLayout(state.phasesReached);
  // Brass accent swell — BOSS_MOOD intensity is a multiplier centered on 1;
  // map the swell to a LOW extra glow alpha (clamped ~0.28) over the base.
  const swell = Math.max(0, Math.min(1, moodIntensity - 1));
  const glowAlpha = (0.18 + swell * 0.1).toFixed(3);
  const hpPct = hpFrac == null ? 100 : Math.round(hpFrac * 100);
  const armour = armourSubline(state, Date.now());

  return (
    <div
      style={{
        position: 'absolute',
        top: 14,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'min(560px, 44vw)',
        minWidth: 320,
        zIndex: 150,
        pointerEvents: 'none',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <style>{BOSSBAR_KEYFRAMES}</style>
      {/* Inner wrapper is remounted (key=shakeSeq) so the shake animation
          restarts cleanly on every phase change without touching the outer
          mount. Reduced-motion disables the shake. */}
      <div
        key={reduceMotion.current ? 'static' : state.shakeSeq}
        style={{
          width: '100%',
          borderRadius: RADII.lg,
          padding: `${SPACE.md}px ${SPACE.lg}px ${SPACE.lg}px`,
          position: 'relative',
          background: `linear-gradient(180deg, ${COLORS.surfaceRaised}, ${COLORS.surface} 55%, ${COLORS.surfaceSunken})`,
          border: `1px solid ${ACCENT.brass}`,
          boxShadow: `${SHADOW.md}, 0 0 14px rgba(196, 164, 105, ${glowAlpha})`,
          animation:
            reduceMotion.current || state.shakeSeq === 0
              ? undefined
              : 'bossbar-shake 0.4s ease-in-out',
        }}
      >
        {/* Brass edge fitting (§2) */}
        <div
          style={{
            position: 'absolute',
            left: SPACE.lg,
            right: SPACE.lg,
            top: 0,
            height: 2,
            borderRadius: 2,
            backgroundImage: `url("${CHROME.panelEdgeLine({ length: 200 })}")`,
            backgroundSize: '100% 100%',
            opacity: 0.55,
          }}
        />

        {/* Header: Cinzel boss name + phase pips */}
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'center',
            gap: SPACE.md,
            marginBottom: SPACE.sm,
          }}
        >
          <span
            style={{
              fontFamily: FONTS.display,
              fontWeight: FONT_WEIGHT.semibold,
              fontSize: FONT_SIZE.xl,
              letterSpacing: '0.05em',
              color: ACCENT.brassBright,
              textShadow: '0 1px 3px rgba(0,0,0,0.7)',
            }}
          >
            {state.npcName}
          </span>
          <span style={{ display: 'inline-flex', gap: SPACE.xs }} aria-label="boss phase progress">
            {Array.from({ length: total }, (_, i) => {
              const n = i + 1;
              const isActive = n === active;
              const isReached = n < active;
              return (
                <span
                  key={n}
                  data-testid={`boss-pip-${n}`}
                  data-active={isActive || undefined}
                  style={{
                    fontFamily: FONTS.mono,
                    fontSize: FONT_SIZE.xs,
                    lineHeight: '14px',
                    padding: '1px 5px',
                    borderRadius: RADII.pill,
                    letterSpacing: '0.06em',
                    color: isActive
                      ? COLORS.surface
                      : isReached
                        ? ACCENT.brassBright
                        : COLORS.textMuted,
                    background: isActive ? ACCENT.brassBright : 'transparent',
                    border: `1px solid ${isActive || isReached ? ACCENT.brass : COLORS.borderSubtle}`,
                  }}
                >
                  {`P${n}`}
                </span>
              );
            })}
          </span>
        </div>

        {/* HP track (semantic health color + top gloss, §2) */}
        <div
          style={{
            height: 12,
            borderRadius: RADII.pill,
            background: COLORS.surfaceSunken,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.6)',
          }}
        >
          <div
            data-testid="boss-hp-fill"
            style={{
              height: '100%',
              width: `${hpPct}%`,
              borderRadius: RADII.pill,
              background: `linear-gradient(180deg, ${COLORS.health}, rgba(0,0,0,0.35))`,
              backgroundColor: COLORS.health,
              transition: `width ${hpFrac == null ? '0s' : '0.18s'} ease-out`,
              position: 'relative',
            }}
          >
            {/* 1px top gloss */}
            <div
              style={{
                position: 'absolute',
                left: 2,
                right: 2,
                top: 1,
                height: 1,
                borderRadius: 1,
                background: 'rgba(255,255,255,0.28)',
              }}
            />
          </div>
        </div>

        {/* Phase-name subtitle (+ the rock armour while it is up) */}
        {(state.phaseName || armour) && (
          <div
            style={{
              marginTop: SPACE.xs,
              textAlign: 'center',
              fontFamily: FONTS.ui,
              fontSize: FONT_SIZE.sm,
              letterSpacing: '0.04em',
              color: COLORS.textSecondary,
            }}
          >
            {state.phaseName}
            {armour && (
              <span data-testid="boss-armour" style={{ color: ACCENT.brassBright, fontWeight: FONT_WEIGHT.semibold }}>
                {state.phaseName ? ' · ' : ''}{armour}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BossBar;
